import React, { useState, useEffect, useRef } from "react";
import ApiClient from "../services/fuelandmoreapi";
import type {
  TCurrentVessel,
  TPort,
  TBunkerPrice,
  TWeather,
  TCrudePrice,
  TPortCalling,
} from "@/types";
import SearchPort from "./SearchPort";
import "./PortPlanner.css";
import WaypointIcon from "./WaypointIcon";
import CalendarIcon from "./CalendarIcon";
import BinIcon from "./BinIcon";

import { config } from "../config";

interface PortPlannerProps {
  vessel: TCurrentVessel;
  onClose: () => void;
  setRouteCoordinates: React.Dispatch<
    React.SetStateAction<[number, number][][]>
  >;
  setRouteColors: React.Dispatch<React.SetStateAction<string[]>>;
  setBunkerMarkers: React.Dispatch<React.SetStateAction<TPort[][]>>;
  setPrices: React.Dispatch<React.SetStateAction<TBunkerPrice[]>>;
  setCrudePrice: React.Dispatch<React.SetStateAction<TCrudePrice | undefined>>;
  setWeather: React.Dispatch<React.SetStateAction<Map<string, TWeather[]>>>;
  distanceNm: number;
  onSelectPortCall: (
    fromPort: string,
    destinationPort: string,
    eta: string
  ) => void; // Updated prop
}

const PortPlanner: React.FC<PortPlannerProps> = ({
  vessel,
  onClose,
  setRouteCoordinates,
  setRouteColors,
  setBunkerMarkers,
  setPrices,
  setCrudePrice, // New prop for Brent crude price
  setWeather,
  distanceNm,
  onSelectPortCall, // Updated prop
}) => {
  const [portCalls, setPortCalls] = useState<TPortCalling[]>([]);
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const toPortInputRefs = useRef<Array<HTMLInputElement | null>>([]); // <-- Add this ref
  const searchPortRef = useRef<HTMLDivElement | null>(null);
  const { apiBaseUrl } = config;
  const apiUrl = apiBaseUrl;

  const [selectedPortCallIndex, setSelectedPortCallIndex] = useState<
    number | null
  >(null);
  const [loadingRouteIndex, setLoadingRouteIndex] = useState<number | null>(
    null
  );
  const [confirmDeletePortCall, setConfirmDeletePortCall] = useState<
    number | null
  >(null);

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
  };

  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
  };

  const formatDateToDDMM = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
  };

  // Add this helper function inside your component (or outside if you prefer)
  const formatDateToDDMMYYYY = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper to ensure updatePortCalls uses the latest array
  const updatePortCallsWithArray = async (calls: TPortCalling[]) => {
    // Only update if the last port calling has a valid port selected and eta is a valid date
    const last = calls[calls.length - 1];
    if (
      !last.portUnlocode ||
      !last.portName ||
      !last.eta ||
      isNaN(Date.parse(last.eta))
    )
      return;
    try {
      const apiClient = new ApiClient(`${apiUrl}`);
      const response = await apiClient.addPortCallings(vessel.imo, calls);
      if (response && response.data) {
        setPortCalls(
          response.data.map((portCall: any) => ({
            portUnlocode: portCall.portUnlocode,
            eta: formatDateForInput(portCall.eta),
            portName: portCall.portName || "Unknown Port",
            latitude: portCall.latitude,
            longitude: portCall.longitude,
            guid: portCall.guid,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to update port callings:", error);
    }
  };

  useEffect(() => {
    const fetchPortCalls = async () => {
      try {
        const apiClient = new ApiClient(`${apiUrl}`);
        const response = await apiClient.getPortCallings(vessel.imo);
        const portCallings = response.data.map((portCall: any) => ({
          portUnlocode: portCall.portUnlocode,
          eta: formatDateForInput(portCall.eta),
          portName: portCall.portName || "Unknown Port",
          latitude: portCall.latitude,
          longitude: portCall.longitude,
          guid: portCall.guid,
        }));
        setPortCalls(portCallings);
      } catch (error) {
        console.error("Failed to fetch port callings:", error);
      }
    };

    fetchPortCalls();
  }, [vessel.imo]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCurrentEditIndex(null);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchPortRef.current &&
        !searchPortRef.current.contains(event.target as Node)
      ) {
        setCurrentEditIndex(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePortSelection = (port: TPort, index: number) => {
    const updatedCalls = [...portCalls];
    updatedCalls[index] = {
      ...updatedCalls[index],
      portUnlocode: port.unlocode,
      portName: port.name,
      latitude: port.latitude,
      longitude: port.longitude,
    };
    setPortCalls(updatedCalls);
    setCurrentEditIndex(null); // Close the SearchPort component
    // If this is the last row and both port and eta are set and eta is a valid date, call updatePortCallsWithArray
    if (
      index === updatedCalls.length - 1 &&
      updatedCalls[index].portUnlocode &&
      updatedCalls[index].portName &&
      updatedCalls[index].eta &&
      !isNaN(Date.parse(updatedCalls[index].eta))
    ) {
      updatePortCallsWithArray(updatedCalls);
    }
  };

  // Focus and select the To Port input when a new port calling is added and expanded
  useEffect(() => {
    if (
      expandedIndex !== null &&
      currentEditIndex === expandedIndex &&
      toPortInputRefs.current[expandedIndex]
    ) {
      const input = toPortInputRefs.current[expandedIndex];
      input?.focus();
      input?.select();
    }
  }, [expandedIndex, currentEditIndex, portCalls.length]);

  const handleAddPortCall = () => {
    setPortCalls((prev) => {
      // Determine the from date for the new port calling
      let fromDate: string;
      if (prev.length === 0) {
        fromDate = getCurrentDate();
      } else {
        fromDate = prev[prev.length - 1].eta || getCurrentDate();
      }

      // Calculate toDate as fromDate + 7 days
      const from = new Date(fromDate);
      from.setDate(from.getDate() + 7);
      const toDate = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}-${String(from.getDate()).padStart(2, "0")}`;

      const newCalls = [
        ...prev,
        { portUnlocode: "", eta: toDate, portName: "New Port" },
      ];
      setExpandedIndex(newCalls.length - 1);
      setCurrentEditIndex(newCalls.length - 1);
      return newCalls;
    });
  };

  const handleDeletePortCall = (index: number) => {
    const updatedCalls = portCalls.filter((_, i) => i !== index);
    setPortCalls(updatedCalls);
    // POST the updated array to the server
    updatePortCallsWithArray(updatedCalls);
  };

  const handlePlanBunkers = async (index: number) => {
    setLoadingRouteIndex(index); // Show progress meter for this port call
    try {
      if (index < 0 || index >= portCalls.length) {
        console.error("Invalid index for Plan Bunkers");
        return;
      }

      const selectedPortCall = portCalls[index];

      if (!selectedPortCall.portName || !selectedPortCall.eta) {
        console.error("Missing port name or ETA for the selected port call");
        return;
      }

      const lastPortCall = portCalls[index - 1];
      if (!lastPortCall) {
        onSelectPortCall(
          "Current location",
          selectedPortCall.portName,
          selectedPortCall.eta
        );
      } else {
        onSelectPortCall(
          lastPortCall.portName ?? "",
          selectedPortCall.portName,
          selectedPortCall.eta
        );
      }

      // Pass the destination port and ETA up to the parent

      let startLatitude: number | undefined;
      let startLongitude: number | undefined;
      let endLatitude: number | undefined;
      let endLongitude: number | undefined;

      if (index === 0) {
        // First row: Use vessel's current position as the starting point
        startLatitude = vessel.latitude;
        startLongitude = vessel.longitude;
        endLatitude = portCalls[index].latitude;
        endLongitude = portCalls[index].longitude;
      } else {
        // Other rows (including the last row): Use the previous port as the starting point
        startLatitude = portCalls[index - 1].latitude;
        startLongitude = portCalls[index - 1].longitude;
        endLatitude = portCalls[index].latitude;
        endLongitude = portCalls[index].longitude;
      }

      if (!startLatitude || !startLongitude || !endLatitude || !endLongitude) {
        console.error(
          "Missing latitude or longitude for one of the ports or vessel"
        );
        return;
      }

      const apiClient = new ApiClient(`${apiUrl}`);
      const response = await apiClient.getRoutesWithPorts(
        startLatitude,
        startLongitude,
        endLatitude,
        endLongitude,
        distanceNm
      );

      console.log("Response:", response);

      // Update route coordinates and bunker markers
      setRouteCoordinates(response.routes); // Assuming response.routes is an array of [lat, lng]
      setBunkerMarkers(response.ports); // Assuming response.ports is an array of ports with lat/lng
      setPrices(
        response.prices.sort((a: TBunkerPrice, b: TBunkerPrice) => {
          if (a.fuelGrade < b.fuelGrade) return -1;
          if (a.fuelGrade > b.fuelGrade) return 1;
          return b.price - a.price; // Sort by price descending
        })
      );
      setCrudePrice(response.crudePrices.BRENT);
      setWeather(response.weather); // Assuming response.weatherForecasts is an array of weather data
      setRouteColors(["#1e3e57", "#1e3e57"]); // Set the route colors
    } catch (error) {
      console.error("Failed to fetch routes and ports:", error);
    } finally {
      setLoadingRouteIndex(null); // Hide progress meter
    }
  };

  // Only call updatePortCalls after initial load (not on mount) and if portCalls have changed from initial load
  const initialPortCallsRef = useRef<TPortCalling[] | null>(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    // Set the initial port calls after they are loaded from the API
    if (initialPortCallsRef.current === null && portCalls.length > 0) {
      initialPortCallsRef.current = portCalls.slice();
    }
  }, [portCalls]);

  // Removed effect that auto-updated port callings on every portCalls change to prevent infinite loop.
  // Call updatePortCalls() only in response to explicit user actions (e.g., Save button, confirm edit, etc.)

  useEffect(() => {
    // Cleanup function to clear route and selection when vessel changes or component unmounts
    return () => {
      setRouteCoordinates([]);
      setRouteColors([]);
      setBunkerMarkers([]);
      setPrices([]);
      setWeather(new Map());
      setSelectedPortCallIndex(null);
    };
  }, [vessel.imo]);

  // Add this simple modal component at the top or bottom of your file if not already present
  const ConfirmModal: React.FC<{
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    message: string;
  }> = ({ open, onConfirm, onCancel, message }) => {
    if (!open) return null;
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-message">{message}</div>
          <div className="modal-actions">
            <button className="modal-btn confirm" onClick={onConfirm}>
              Yes
            </button>
            <button className="modal-btn cancel" onClick={onCancel}>
              No
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="port-planner-container"
      onClick={(e) => {
        e.stopPropagation(); // Prevent click from propagating to the parent
      }}
    >
      <div className="port-planner-heading">
        {portCalls.length > 0 ? (
          <em>Port Callings:</em>
        ) : (
          <em>No Port Callings</em>
        )}
      </div>
      <div className="port-planner-card">
        {portCalls.map((call, index) => {
          if (!call.portName || call.portName.trim() === "") {
            return null;
          }
          const fromPort =
            index === 0
              ? { portName: "Current Position", eta: getCurrentDate() }
              : portCalls[index - 1];
          const toPort = call;

          // --- CHANGED: Make the summary clickable for expand/collapse ---
          // --- CHANGED: Truncate port name and show tooltip on hover ---
          const maxPortNameLength = 14;
          const portName = toPort.portName || "";
          const isTruncated = portName.length > maxPortNameLength;
          const displayPortName = isTruncated
            ? portName.slice(0, maxPortNameLength) + "..."
            : portName;

          return (
            <div key={index}>
              <div
                className="port-call-summary"
                style={{
                  position: "relative",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (expandedIndex === index) {
                    // Collapsing: clear route and selection
                    setExpandedIndex(null);
                    setRouteCoordinates([]);
                    setRouteColors([]);
                    setBunkerMarkers([]);
                    setPrices([]);
                    setWeather(new Map());
                    setSelectedPortCallIndex(null);
                  } else {
                    setExpandedIndex(index);
                  }
                }}
              >
                <span>
                  <span
                    title={isTruncated ? portName : undefined}
                    style={
                      isTruncated
                        ? { textDecoration: "underline dotted", cursor: "help" }
                        : undefined
                    }
                  >
                    {displayPortName}
                  </span>
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    minWidth: 60,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatDateToDDMM(toPort.eta)}
                </span>
                <button
                  className="expand-collapse-btn"
                  aria-label={expandedIndex === index ? "Collapse" : "Expand"}
                  tabIndex={-1}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (expandedIndex === index) {
                      setExpandedIndex(null);
                      setRouteCoordinates([]);
                      setRouteColors([]);
                      setBunkerMarkers([]);
                      setPrices([]);
                      setWeather(new Map());
                      setSelectedPortCallIndex(null);
                    } else {
                      setExpandedIndex(index);
                    }
                  }}
                >
                  <img
                    src={
                      expandedIndex === index ? "/opened.svg" : "/closed.svg"
                    }
                    alt={expandedIndex === index ? "Collapse" : "Expand"}
                    className="accordion-icon"
                  />
                </button>
              </div>
              {expandedIndex === index && (
                <div className="port-call-detail" style={{ marginTop: 16 }}>
                  <div
                    className={`port-call-box ${selectedPortCallIndex === index ? "secondarySelectedBox" : ""}`}
                  >
                    <div className="port-call-row">
                      <label>From</label>
                      <div className="port-name-wrapper">
                        <span>
                          <WaypointIcon />
                        </span>
                        <span className="port-name-display read-only-label">
                          {fromPort.portName || ""}
                        </span>
                      </div>
                      <div className="eta-wrapper">
                        <span>
                          <CalendarIcon />
                        </span>
                        <span className="eta-label read-only-label">
                          {fromPort.eta
                            ? formatDateToDDMMYYYY(fromPort.eta)
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="port-call-row">
                      <label>To</label>
                      <div className="port-name-wrapper">
                        <span>
                          <WaypointIcon />
                        </span>
                        <div>
                          {index === portCalls.length - 1 ? (
                            currentEditIndex === index ? (
                              <div
                                className="search-port-container"
                                ref={searchPortRef}
                              >
                                <SearchPort
                                  onSelect={(port) => {
                                    handlePortSelection(port, index);
                                    setCurrentEditIndex(null);
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className="port-name-display read-only-label"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentEditIndex(index);
                                }}
                                style={{ cursor: "pointer" }}
                                title={
                                  toPort.portName || "Click to select port"
                                }
                              >
                                {toPort.portName || "Click to select port"}
                              </div>
                            )
                          ) : (
                            <div
                              className="port-name-display read-only-label"
                              title={toPort.portName || "Click to select port"}
                            >
                              {toPort.portName || "Click to select port"}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="eta-wrapper">
                        <span>
                          <CalendarIcon />
                        </span>
                        {index === portCalls.length - 1 ? (
                          <input
                            type="date"
                            value={
                              toPort.eta ? formatDateForInput(toPort.eta) : ""
                            }
                            className="eta-input"
                            onChange={(e) => {
                              const newEta = e.target.value;
                              setPortCalls((prev) => {
                                const updated = prev.map((c, i) =>
                                  i === index ? { ...c, eta: newEta } : c
                                );
                                // If this is the last row and port is set, call updatePortCallsWithArray with the new array
                                if (
                                  index === prev.length - 1 &&
                                  updated[index].portUnlocode &&
                                  updated[index].portName
                                ) {
                                  setTimeout(
                                    () => updatePortCallsWithArray(updated),
                                    0
                                  );
                                }
                                return updated;
                              });
                            }}
                          />
                        ) : (
                          <span className="eta-label read-only-label">
                            {toPort.eta ? formatDateToDDMMYYYY(toPort.eta) : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="view-route-row">
                      <a
                        href="#"
                        className="view-route-link"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedPortCallIndex((prev) =>
                            prev === index ? null : index
                          );
                          await handlePlanBunkers(index);
                        }}
                        style={{
                          pointerEvents:
                            loadingRouteIndex === index ? "none" : undefined,
                        }}
                      >
                        View route(s) &gt;
                      </a>
                      {loadingRouteIndex === index && (
                        <div
                          className="route-loading-spinner"
                          aria-label="Loading route"
                        >
                          <div className="spinner"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="action-buttons">
                    <button
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeletePortCall(index);
                      }}
                    >
                      <BinIcon color="black" />
                    </button>
                  </div>
                </div>
              )}
              {/* Confirm modal for port calling deletion */}
              <ConfirmModal
                open={confirmDeletePortCall === index}
                message="Are you sure you want to delete this port calling?"
                onConfirm={() => {
                  handleDeletePortCall(index);
                  setConfirmDeletePortCall(null);
                }}
                onCancel={() => setConfirmDeletePortCall(null)}
              />
            </div>
          );
        })}
        <div className="footer-buttons">
          <button
            className="primary-button"
            onClick={(e) => {
              e.stopPropagation();
              handleAddPortCall();
            }}
          >
            + Add Port Calling
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortPlanner;
